'use client';

import React, { useEffect, useState } from 'react';
import { registerContact } from '@/actions';
import { HubspotFormData } from '@/interfaces';
import { RichTextField } from '@prismicio/client';
import {
  Controller,
  FieldValues,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import cookies from 'js-cookie';
import { PrismicRichText } from '@prismicio/react';
import { CheckIcon, ArrowRightIcon } from '@/assets';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { emailRegex, isCorporateEmail } from '@/utils';

interface Props {
  formData: HubspotFormData;
  hubspotPortalIdForm: string;
  hubspotGuidForm: string;
  formTitle?: RichTextField;
  formDescription?: RichTextField;
  renderTerms?: boolean;
  redirect?: boolean;
  confirmationPageUid?: string;
  utmEbook?: string;
  formContainerClass?: string;
}

const getFieldType = (fieldType: string) => {
  switch (fieldType) {
    case 'single_line_text':
    default:
      return 'text';
    case 'phone':
      return 'tel';
    case 'email':
      return 'email';
  }
};

export const FormLayout = ({
  formData,
  hubspotPortalIdForm,
  hubspotGuidForm,
  formTitle,
  formDescription,
  renderTerms = true,
  redirect = false,
  confirmationPageUid = '',
  utmEbook = '',
  formContainerClass = 'flex flex-col gap-y-6',
}: Props) => {
  const { fieldGroups } = formData;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FieldValues>({ mode: 'onBlur' });
  const router = useRouter();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleWindowClick = () => setOpenDropdown(null);
    window.addEventListener('click', handleWindowClick);
    return () => window.removeEventListener('click', handleWindowClick);
  }, []);

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (executeRecaptcha) {
      try {
        const token = await executeRecaptcha('onSubmit');

        const response = await fetch('/api/validate_token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        const verificationToken = await response.json();

        if (verificationToken.success && verificationToken.score > 0.6) {
          reset();

          delete data.terms;

          await registerContact({
            properties: data,
            pageUri: `https://devsu.com${window.location.pathname}`,
            pageName: window.document.title,
            hubspotPortalIdForm: hubspotPortalIdForm,
            hubspotGuidForm: hubspotGuidForm,
            hubspotutkCookie: cookies.get('hubspotutk'),
          });

          setIsSubmitSuccessful(true);

          if (utmEbook) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('utm', utmEbook);
            window.history.replaceState({}, '', currentUrl.toString());
          }

          if (redirect) {
            const url = utmEbook
              ? `/${confirmationPageUid}?utm=${utmEbook}`
              : `/${confirmationPageUid}`;
            router.push(url);
          }
        } else {
          setIsSubmitSuccessful(false);
        }
      } catch (error) {
        console.error('reCAPTCHA failed:', error);
        setErrorMessage('Something went wrong. Please try again later.');
        setIsSubmitSuccessful(false);
        return;
      }
    }
  };

  if (isSubmitSuccessful && !redirect) {
    return (
      <div className="flex flex-col gap-y-6 items-center justify-center h-full text-center">
        <CheckIcon />
        <p className="font-bold text-medium-normal">
          Your form has been received. Thanks for choosing Devsu!
        </p>
        <p>We&apos;ll reach out soon.</p>
        <p>
          While you wait, <span className="font-bold">check out our</span>{' '}
          <Link href="/blog">
            <span className="text-primary-shade-green font-bold">blog.</span>
          </Link>
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={formContainerClass}
      noValidate
    >
      {formTitle && (
        <div className="text-left">
          <PrismicRichText field={formTitle} />
        </div>
      )}
      {formDescription && (
        <div className="text-left">
          <PrismicRichText
            field={formDescription}
            components={{
              paragraph: ({ children }) => (
                <p className="text-medium-normal">{children}</p>
              ),
            }}
          />
        </div>
      )}
      {fieldGroups.map(({ fields }, index) => (
        <div key={`field-group-${index}`} className="flex gap-4">
          {fields.map(
            (
              { label, required, fieldType, name, placeholder, options },
              fieldIndex,
            ) => (
              <div key={`field-${fieldIndex}`} className="flex-1">
                {fieldType === 'dropdown' && options ? (
                  <>
                    <Controller
                      name={name}
                      control={control}
                      rules={{
                        required: {
                          value: required,
                          message: `${label} is required`,
                        },
                      }}
                      render={({ field: { value, onChange } }) => {
                        const selected = options.find((o) => o.value === value);
                        const isOpen = openDropdown === name;
                        return (
                          <div
                            className="relative"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              aria-haspopup="listbox"
                              aria-expanded={isOpen}
                              aria-controls={
                                isOpen ? `${name}-listbox` : undefined
                              }
                              onClick={() =>
                                setOpenDropdown(isOpen ? null : name)
                              }
                              className="p-3 w-full border border-gray-400 text-white rounded-lg bg-transparent outline-none text-left"
                            >
                              <span className={selected ? '' : 'text-gray-300'}>
                                {selected
                                  ? selected.label
                                  : placeholder || label}
                              </span>
                            </button>
                            {isOpen && (
                              <ul
                                id={`${name}-listbox`}
                                role="listbox"
                                className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-500 bg-[#0d1918] shadow-lg"
                              >
                                {options.map((option, idx) => {
                                  const isSelected = value === option.value;
                                  return (
                                    <li
                                      key={idx}
                                      id={`${name}-option-${option.value}`}
                                      role="option"
                                      aria-selected={isSelected}
                                    >
                                      <button
                                        type="button"
                                        className={`w-full text-left px-3 py-2 hover:bg-white/10 ${
                                          isSelected ? 'bg-white/10' : ''
                                        }`}
                                        onClick={() => {
                                          onChange(option.value);
                                          setOpenDropdown(null);
                                        }}
                                      >
                                        {option.label}
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        );
                      }}
                    />
                    {errors[name] && (
                      <span className="text-error text-sm mt-1 px-3">
                        {errors[name]?.message as string}
                      </span>
                    )}
                  </>
                ) : name === 'scope' ? (
                  <>
                    <textarea
                      rows={4}
                      placeholder={placeholder || label}
                      className="mt-1 p-3 block w-full border border-gray-400 rounded-md text-white outline-none bg-transparent placeholder-gray-300"
                      {...register(name, {
                        required: {
                          value: required,
                          message: `${label} is required`,
                        },
                      })}
                      aria-label={label}
                    />
                    {errors[name] && (
                      <span className="text-error text-sm mt-1 px-3">
                        {errors[name]?.message as string}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      type={getFieldType(fieldType)}
                      id={name}
                      placeholder={placeholder || label}
                      {...register(name, {
                        required: {
                          value: required,
                          message: `${label} is required`,
                        },
                        ...(fieldType === 'email' && {
                          pattern: {
                            value: emailRegex,
                            message: 'Enter a valid email',
                          },
                          validate: {
                            isCorporate: (value) =>
                              isCorporateEmail(value) ||
                              'Please use a corporate email address',
                          },
                        }),
                      })}
                      className="p-3 block w-full border border-gray-400 rounded-lg text-white outline-none bg-transparent placeholder-gray-300"
                      aria-label={label}
                    />
                    {errors[name] && (
                      <span className="text-error text-sm mt-1 px-3">
                        {errors[name]?.message as string}
                      </span>
                    )}
                  </>
                )}
              </div>
            ),
          )}
        </div>
      ))}
      {renderTerms && (
        <div className="flex flex-col">
          <div className="flex items-center gap-x-4">
            <input
              type="checkbox"
              id="terms"
              {...register('terms', {
                required: {
                  value: true,
                  message: 'You should accept Terms and Conditions',
                },
              })}
              className="cursor-pointer"
              aria-label="Accept Terms and Conditions"
            />
            <label htmlFor="terms" className="cursor-pointer text-white">
              I accept the{' '}
              <Link
                href="/terms-and-conditions"
                className="text-green-400 underline hover:text-green-300"
              >
                Terms and conditions
              </Link>
              .
            </label>
          </div>
          {errors['terms'] && (
            <span className="text-error text-sm mt-1">
              {errors['terms']?.message as string}
            </span>
          )}
        </div>
      )}

      <button
        className="secondary-button w-full disabled:opacity-50"
        type="submit"
        disabled={isSubmitting}
        aria-label="Submit form"
      >
        <ArrowRightIcon className="arrow-icon" />
        <span>{formData.displayOptions.submitButtonText}</span>
      </button>

      {errorMessage && (
        <span className="text-error text-sm mt-1">{errorMessage}</span>
      )}
    </form>
  );
};
